/* eslint-disable react-hooks/exhaustive-deps */
import { literals } from "@/constants/literals";
import { useEffect, useState } from "react";

import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import useExitsDataStore from "@/store/useExitsData.store";
import { useNavigate, useParams } from "react-router-dom";
import type { CapturedPhoto, GroupData, ExitsStep } from "@/types/types.d";
import { CameraCapture } from "../../components/CameraCapture";
import { BASE_URL } from "@/constants/constants";
import ExitsOrderHeader from "../components/ExitsOrderHeader";
import { useDevice } from "@/store/useDevice";
import ExitsGroupMobileInputs from "../components/ExitsGroupMobileInputs";
import ExitsGroupTabletInputs from "../components/ExitsGroupTabletInputs";

const initial_status = {
  description: "",
  extraInformation: "",
  visibleForClient: true,
  photos: [],
  id: "",
};

const ExistEditionGroupPage = () => {
  const { groupId } = useParams() as { groupId: string };
  const [groupData, setGroupData] = useState<GroupData>(initial_status);
  const [description, setDescription] = useState("");
  const [extraInformation, setExtraInformation] = useState("");
  const { exitsData, exitsGroupData, updateExitsGroupData } =
    useExitsDataStore();
  const navigate = useNavigate();

  const { isMobile } = useDevice();

  const handleOnChange = (
    id: string,
    value: string | boolean | CapturedPhoto[],
  ) => {
    setGroupData((prevState) => {
      const tmp: any = { ...prevState };
      tmp[id] = value;
      return tmp;
    });
  };

  const changeDescription = (value: string) => {
    setDescription(value);
    if (value === "") {
      handleOnChange("description", "");
      return;
    }
    const entireDescription =
      extraInformation.trim() !== "" ? `${value}: ${extraInformation}` : value;
    handleOnChange("description", entireDescription);
  };

  const changeExtraInformation = (value: string) => {
    setExtraInformation(value);
    if (value.trim() === "") {
      handleOnChange("description", description);
    } else {
      handleOnChange("description", `${description}: ${value}`);
    }
  };

  const isValid = () => {
    if (groupData.photos.length === 0) {
      return true;
    }
    if (exitsData.step === literals.packagingAction) {
      return description.trim() !== "";
    }
    return true;
  };

  const handleOnSubmit = (
    e: React.BaseSyntheticEvent<
      Event,
      EventTarget & HTMLFormElement,
      EventTarget
    >,
  ) => {
    e.preventDefault();
    if (!isValid()) return;
    updateExitsGroupData({ ...groupData, id: Number(groupId) });
    navigate(`${BASE_URL}/capture/exits/resume`);
  };

  useEffect(() => {
    const groupData = exitsGroupData.find(
      (group) => group.id.toString() === groupId,
    );
    if (groupData) {
      setDescription(groupData.description.split(":")[0]);
      setExtraInformation(groupData.description.split(":")[1]?.trim() || "");
      setGroupData({
        description: groupData.description,
        visibleForClient: groupData.visibleForClient,
        photos: groupData.photos,
      });
    }
  }, []);

  return (
    <div className="min-h-fulldpx flex flex-col justify-between bg-ib-pt-color-white">
      <ExitsOrderHeader />
      <form
        onSubmit={handleOnSubmit}
        className="flex flex-col flex-1 justify-between"
      >
        <div className="w-full px-4 md:px-14 pt-4 md:flex-1 md:flex md:flex-col md:justify-between">
          <div className="w-full flex-1">
            <CameraCapture
              onChange={(photos) => handleOnChange("photos", photos)}
              maxPhotos={10}
              photos={groupData?.photos || undefined}
            />
          </div>
          {isMobile ? (
            <ExitsGroupMobileInputs
              step={exitsData.step as ExitsStep}
              groupData={groupData}
              description={description}
              extraInformation={extraInformation}
              changeGroupData={handleOnChange}
              changeDescription={changeDescription}
              changeExtraInformation={changeExtraInformation}
            />
          ) : (
            <ExitsGroupTabletInputs
              step={exitsData.step as ExitsStep}
              groupData={groupData}
              description={description}
              extraInformation={extraInformation}
              changeGroupData={handleOnChange}
              changeDescription={changeDescription}
              changeExtraInformation={changeExtraInformation}
            />
          )}
        </div>
        <div className="py-4 flex flex-col pt-4 border-t border-t-ib-pt-color-gray-300 md:flex md:flex-row md:justify-center">
          <div className="px-4 md:capture-button">
            <PrimaryButton
              size="medium"
              label={literals.saveChanges}
              colour="red"
              type="submit"
              onClick={() => {}}
              disabled={!isValid()}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default ExistEditionGroupPage;
